import { useState, useEffect } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { PublicHeader, PublicFooter } from "@/components/layouts/PublicLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Loader2, LogIn, Eye, EyeOff, X } from "lucide-react";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Logo URL
const LOGO_URL = "https://customer-assets.emergentagent.com/job_712ba195-f8b2-4b91-a610-23f4aedf7ae0/artifacts/i0b4179m_A_I_Means_Business.jpg";

const LoginPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login } = useAuth();
  
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  
  // Get redirect and prefilled email from URL params
  const redirectTo = searchParams.get("redirect") || "/portal";
  const prefilledEmail = searchParams.get("email");
  
  useEffect(() => {
    if (prefilledEmail) {
      setEmail(prefilledEmail);
    }
  }, [prefilledEmail]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email || !password) {
      toast.error("Please enter your email and password");
      return;
    }
    
    setIsLoading(true);
    
    try {
      const response = await axios.post(`${API}/auth/login`, {
        email,
        password
      });
      
      const { access_token, user } = response.data;
      
      // Store token and user info
      login(access_token, user);
      
      toast.success("Welcome back!");
      
      // Redirect based on role (use setTimeout to ensure state is updated)
      setTimeout(() => {
        if (user.role === "admin") {
          navigate("/admin", { replace: true });
        } else {
          navigate(redirectTo, { replace: true });
        }
      }, 100);
      
    } catch (error) {
      const message = error.response?.data?.detail || "Invalid email or password";
      toast.error(message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    navigate("/");
  };

  return (
    <div className="min-h-screen bg-background">
      <PublicHeader />

      <section className="py-24 bg-gradient-to-b from-navy-950 to-navy-900">
        <div className="max-w-md mx-auto px-6">
          {/* Login Card - White background with dark text */}
          <div className="bg-white p-8 rounded-2xl shadow-2xl">
            {/* Logo */}
            <div className="flex justify-center mb-6">
              <img 
                src={LOGO_URL} 
                alt="A.I. Means Business" 
                className="h-16 w-auto"
              />
            </div>

            {/* Header */}
            <div className="text-center mb-6">
              <h1 className="font-heading text-2xl font-bold text-navy-900 mb-2">
                Sign In
              </h1>
              <p className="text-slate-600 text-sm">
                Access your AI Means Business portal
              </p>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="email" className="text-navy-900 text-sm font-medium">
                  Email Address
                </Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="your@email.com"
                  className="bg-slate-50 border-slate-300 text-navy-900 mt-1 focus:border-primary focus:ring-primary"
                  autoComplete="email"
                  data-testid="login-email"
                />
              </div>

              <div>
                <Label htmlFor="password" className="text-navy-900 text-sm font-medium">
                  Password
                </Label>
                <div className="relative mt-1">
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Enter your password"
                    className="bg-slate-50 border-slate-300 text-navy-900 pr-10 focus:border-primary focus:ring-primary"
                    autoComplete="current-password"
                    data-testid="login-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-navy-900"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <div className="flex gap-3 pt-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleCancel}
                  className="flex-1 border-slate-300 text-navy-900 hover:bg-slate-100 hover:text-navy-900"
                  data-testid="login-cancel"
                >
                  <X className="w-4 h-4 mr-2" />
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={isLoading}
                  className="flex-1 bg-primary hover:bg-primary/90 text-white"
                  data-testid="login-submit"
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Signing In...
                    </>
                  ) : (
                    <>
                      <LogIn className="w-4 h-4 mr-2" />
                      Sign In
                    </>
                  )}
                </Button>
              </div>
            </form>

            {/* Forgot Password Link */}
            <div className="text-center mt-6">
              <Link 
                to="/forgot-password" 
                className="text-primary text-sm hover:underline"
              >
                Forgot your password?
              </Link>
            </div>
          </div>

          {/* Help Text */}
          <p className="text-center text-slate-400 text-xs mt-6">
            Don't have an account? <Link to="/get-started" className="text-primary hover:underline">Get started</Link> with your free evaluation.
          </p>
        </div>
      </section>

      <PublicFooter />
    </div>
  );
};

export default LoginPage;
